import React, { useState, useEffect, useMemo } from "react";
import { Modal, ConfigProvider, theme } from 'antd';
import * as Style from "../styles/GroupSettings.style";

interface EditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (time: string) => void;
    defaultTime: string;
}

const TimeBrightModal: React.FC<EditModalProps> = ({
    isOpen,
    onClose,
    onSave,
    defaultTime,
}) => {

    const [hour, setHour] = useState<number>(12);  // 시간 (1-12)
    const [minute, setMinute] = useState<number>(0);  // 분 (0, 30)
    const [period, setAmpm] = useState<string>('AM');  // AM/PM

    const [tempTime, setTempTime] = useState(defaultTime);

    // TimeBrightModal 컴포넌트 내부에 추가 또는 주석 해제 후 수정
    const parseTime = (timeStr: string) => {
        // 예: "9:30 AM" 또는 "12:00 PM"
        const parts = timeStr.trim().split(/\s+/); // 공백을 기준으로 시간과 AM/PM 분리
        
        // 만약 timeStr이 유효하지 않거나 24시간 형식("HH:MM")으로 넘어올 경우를 대비한 안전 장치
        if (parts.length < 2) {
            // 24시간 형식("HH:MM")이라고 가정하고 변환을 시도하거나, 기본값 반환
            const [h24, m] = timeStr.split(':').map(val => Number(val) || 0);
            let h12 = h24 % 12 === 0 ? 12 : h24 % 12;
            let period = h24 >= 12 ? 'PM' : 'AM';
            
            return { 
                hour: h12, 
                minute: m, 
                period: period 
            };
        }

        const [hm] = parts;
        const period = parts[1]; // AM 또는 PM
        const [h, m] = hm.split(":").map(val => Number(val) || 0);

        return { hour: h, minute: m, period: period };
    };

    // ✅ defaultTime, defaultBrightness가 변경될 때마다 상태 갱신
    useEffect(() => {
        setTempTime(defaultTime);

        // ⭐️ 추가된 핵심 로직: defaultTime을 파싱하여 Timepicker 상태에 반영 ⭐️
        if (defaultTime) { // defaultTime이 유효한 값일 경우에만 실행
            const { hour: h, minute: m, period: p } = parseTime(defaultTime);
            setHour(h);
            setMinute(m);
            setAmpm(p);
        } else {
            // defaultTime이 빈 값(예: "")으로 넘어왔을 때 기본값으로 초기화
            setHour(12);
            setMinute(0);
            setAmpm('AM');
        }        
    }, [defaultTime]);  
    
    // 렌더링 시 AM/PM 표시 형식을 만드는 함수
    const formatTime12Hour = (time24: string) => {
        const [h24, m] = time24.split(':').map(Number);
        const ampm = h24 >= 12 ? "PM" : "AM";
        const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
        return `${h12}:${String(m).padStart(2, "0")} ${ampm}`; // 예: "9:30 AM"
    };


    // 4️⃣ 선택값 변경 시 tempTime 문자열 업데이트
    const updateTime = (newHour?: number, newMinute?: number, newPeriod?: string) => {
        let h = newHour ?? hour;
        let m = newMinute ?? minute;
        let p = newPeriod ?? period;

        // 24시간 기준 값 변환 후 문자열 생성
        let h24 = p === "PM" ? (h % 12) + 12 : h % 12;
        const formatted24Hour = `${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        //console.log('convert => ',formatted24Hour);

        // 12시간 형식 문자열 생성
        const newTime12Hour = formatTime12Hour(formatted24Hour);
        
        // 상태 업데이트 요청 (이 부분은 필수가 아닐 수 있지만, tempTime을 최신 상태로 유지하려면 필요)
        setTempTime(newTime12Hour);
        
        // onSave에 사용할 새로운 시간 문자열을 즉시 반환
        return newTime12Hour;
    };


    const handleSaveClick = () => {
        // updateTime을 호출하고 반환된 최신 시간 문자열을 immediatelyTime 변수에 저장
        const immediatelyTime = updateTime(hour, minute, period);
        onSave(immediatelyTime);
        //console.log('result => ',immediatelyTime);
    };


    // 시간 선택 (1 - 12)
    const handleHourChange = (direction: 'up' | 'down') => {
        setHour(prevHour => {
        if (direction === 'up') {
            return prevHour === 12 ? 1 : prevHour + 1;
        } else {
            return prevHour === 1 ? 12 : prevHour - 1;
        }
        });
    };

    // 분 선택 (0, 30)
    const handleMinuteChange = (direction: 'up' | 'down') => {
        setMinute(prevMinute => {
        if (direction === 'up') {
            return prevMinute === 0 ? 30 : 0;
        } else {
            return prevMinute === 30 ? 0 : 30;
        }
        });
    };

    // AM/PM 선택
    const handleAmpmChange = (direction: 'up' | 'down') => {
        setAmpm(prevAmpm => (prevAmpm === 'AM' ? 'PM' : 'AM'));
    };

  return (
    <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
    >
        <Style.MotionModeSetListTitle>시간 및 밝기 설정</Style.MotionModeSetListTitle>

        <br></br><br></br>


        <Style.MonthlyModalTimepickerContainer>
            <Style.MonthlyModalTimepickerFrame>

                <Style.MonthlyModalTimepickerTimeFrame>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleHourChange('up')}>▲</Style.MonthlyModalTimepickerButton>
                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>

                    <Style.MonthlyModalTimepickerTimeText>
                        {hour}
                    </Style.MonthlyModalTimepickerTimeText>

                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleHourChange('down')}>▼</Style.MonthlyModalTimepickerButton>
                </Style.MonthlyModalTimepickerTimeFrame>

                <Style.MonthlyModalTimepickerTimeFrame>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleMinuteChange('up')}>▲</Style.MonthlyModalTimepickerButton>
                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>

                    <Style.MonthlyModalTimepickerTimeText>
                        {minute === 0 ? '00' : '30'}
                    </Style.MonthlyModalTimepickerTimeText>

                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleMinuteChange('down')}>▼</Style.MonthlyModalTimepickerButton>
                </Style.MonthlyModalTimepickerTimeFrame>         

                <Style.MonthlyModalTimepickerTimeFrame>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleAmpmChange('up')}>▲</Style.MonthlyModalTimepickerButton>
                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>

                    <Style.MonthlyModalTimepickerTimeText>
                        {period}
                    </Style.MonthlyModalTimepickerTimeText>

                    <Style.MonthlyModalTimepickerTimeLine></Style.MonthlyModalTimepickerTimeLine>
                    <Style.MonthlyModalTimepickerButton onClick={() => handleAmpmChange('down')}>▼</Style.MonthlyModalTimepickerButton>
                </Style.MonthlyModalTimepickerTimeFrame>

            </Style.MonthlyModalTimepickerFrame>
        </Style.MonthlyModalTimepickerContainer>

        <br></br><br></br>

        <Style.MonthlyModalButtonFrame>
            <Style.MonthlyModalCancleButton
                onClick={onClose}
            >
                취소
            </Style.MonthlyModalCancleButton>            
            <Style.MonthlyModalConfirmButton
                onClick={handleSaveClick}
            >
                선택완료
            </Style.MonthlyModalConfirmButton>
        </Style.MonthlyModalButtonFrame>

    </Modal>
  );
};

export default TimeBrightModal;
